import { Link } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { useState } from 'react';

import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { usePuzzles, useDeletePuzzle, type PuzzleEvent } from '@/hooks/usePuzzles';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useToast } from '@/hooks/useToast';
import { Cell } from '@/lib/puzzle';

function PuzzleCard({ pe }: { pe: PuzzleEvent }) {
  const { puzzle, naddr, pubkey, dTag } = pe;
  const author = useAuthor(pubkey);
  const { user } = useCurrentUser();
  const { toast } = useToast();
  const { mutateAsync: deletePuzzle } = useDeletePuzzle();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const metadata = author.data?.metadata;
  const authorName = metadata?.name ?? metadata?.display_name ?? pubkey.slice(0, 8);
  const authorPic = metadata?.picture;
  const isOwn = user?.pubkey === pubkey;

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deletePuzzle({ pubkey, dTag });
      toast({ title: 'Puzzle deleted', description: 'Your puzzle has been removed from Nostr.' });
      setDeleteDialogOpen(false);
    } catch (err) {
      toast({
        title: 'Failed to delete',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card className="flex flex-col overflow-hidden hover:shadow-lg transition-shadow">
      {/* Mini grid preview */}
      <Link to={`/play/${naddr}`} className="block">
        <div className="bg-slate-100 dark:bg-slate-800 p-3">
          <div
            className="grid gap-0.5 mx-auto"
            style={{
              gridTemplateColumns: `repeat(${puzzle.width}, minmax(0, 1fr))`,
              maxWidth: 200,
            }}
          >
            {puzzle.cells.map((cell, i) => {
              const x = i % puzzle.width;
              const y = Math.floor(i / puzzle.width);
              const isStart = x === puzzle.startX && y === puzzle.startY;
              if (cell === Cell.Wall) {
                return <div key={i} className="aspect-square rounded-sm bg-gray-800 dark:bg-gray-950" />;
              }
              if (cell === Cell.Passive) {
                return <div key={i} className="aspect-square rounded-sm bg-gray-300 dark:bg-gray-600" />;
              }
              if (isStart) {
                return <div key={i} className="aspect-square rounded-sm bg-yellow-400" />;
              }
              return <div key={i} className="aspect-square rounded-sm bg-sky-300 dark:bg-sky-600" />;
            })}
          </div>
        </div>
      </Link>

      <CardHeader>
        <Link to={`/play/${naddr}`}>
          <CardTitle className="hover:text-primary transition-colors">{puzzle.title}</CardTitle>
        </Link>
        {puzzle.description && (
          <CardDescription className="line-clamp-2">{puzzle.description}</CardDescription>
        )}
      </CardHeader>

      <CardFooter className="mt-auto flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Avatar className="size-6 shrink-0">
            {authorPic && <AvatarImage src={authorPic} alt={authorName} />}
            <AvatarFallback className="text-xs">{authorName.slice(0, 2).toUpperCase()}</AvatarFallback>
          </Avatar>
          <span className="text-xs text-muted-foreground truncate">{authorName}</span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {isOwn ? (
            <>
              <Button asChild size="sm" variant="ghost" className="h-7 px-2 text-xs">
                <Link to={`/editor?naddr=${naddr}`}>Edit</Link>
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs text-destructive hover:text-destructive"
                onClick={() => setDeleteDialogOpen(true)}
              >
                Delete
              </Button>
            </>
          ) : (
            <>
              <Badge variant="secondary" className="text-xs">
                {puzzle.width}{'\u00d7'}{puzzle.height}
              </Badge>
              {puzzle.somaria && (
                <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">
                  Somaria
                </Badge>
              )}
            </>
          )}
        </div>
      </CardFooter>

      {/* Delete confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{puzzle.title}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This publishes a NIP-09 deletion event to Nostr. Relays that honor deletion requests
              will remove this puzzle. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function PuzzleSkeleton() {
  return (
    <Card className="overflow-hidden">
      <div className="bg-slate-100 dark:bg-slate-800 p-3">
        <Skeleton className="h-32 w-full rounded-lg" />
      </div>
      <CardHeader>
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-4 w-48 mt-1" />
      </CardHeader>
      <CardFooter>
        <Skeleton className="h-4 w-20" />
      </CardFooter>
    </Card>
  );
}

export default function BrowsePage() {
  const { data: puzzles, isLoading, isError, refetch } = usePuzzles(50);

  useSeoMeta({
    title: 'Browse — Somaria Tiles',
    description: 'Discover Hamilton Path tile puzzles shared on Nostr.',
  });

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">Browse Puzzles</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Discover tile puzzles shared by the Nostr community.
            </p>
          </div>
          <div className="flex gap-2">
            <Button asChild variant="outline" size="sm">
              <Link to="/editor">Create</Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/play">Play</Link>
            </Button>
          </div>
        </div>

        {isLoading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <PuzzleSkeleton key={i} />
            ))}
          </div>
        )}

        {isError && (
          <Card className="border-dashed">
            <CardContent className="py-12 px-8 text-center">
              <p className="text-muted-foreground mb-4">
                Failed to load puzzles. Check your relay connections.
              </p>
              <Button variant="outline" onClick={() => refetch()}>Retry</Button>
            </CardContent>
          </Card>
        )}

        {puzzles && puzzles.length === 0 && (
          <Card className="border-dashed">
            <CardContent className="py-12 px-8 text-center">
              <p className="text-muted-foreground mb-4">
                No puzzles found yet. Be the first to share one!
              </p>
              <Button asChild>
                <Link to="/editor">Create a Puzzle</Link>
              </Button>
            </CardContent>
          </Card>
        )}

        {puzzles && puzzles.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {puzzles.map((pe) => (
              <PuzzleCard key={`${pe.pubkey}:${pe.dTag}`} pe={pe} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
